/* eslint-disable */
import { useEffect, useState, useRef, useCallback } from "react";
import { useEcomStore } from "@/context/ecom/EcomContext";
import { useLogin } from "@/context/login/LoginContext";
import { isUserAuthenticated } from "@/context/login/reducer";
import { jwtDecode } from "jwt-decode";
import { emitGTMEvent, syncStoredUserInfo } from "@/components/client/Datalayer/utilities";
import { markUserAsKnown } from "@/lib/knownUserCookie";
const useAuthTID = ({
  path,
  redirect_uri,
  code_verifier,
  client_id,
  url,
  tokenEndpoint
}) => {
  const [state, dispatch] = useEcomStore() ?? [];
  const loginCtx = useLogin();
  const [loginState, loginDispatch] = loginCtx ?? [];
  const isAuthenticated = isUserAuthenticated(loginState);

  // Read OAuth callback params from the URL once on mount.
  // Avoids useSearchParams() which triggers a Suspense CSR bailout
  // when called outside a <Suspense> boundary.
  const [code, setCode] = useState(null);
  const [error, setError] = useState(null);
  const [urlState, setUrlState] = useState(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    setCode(sp.get("code"));
    setError(sp.get("error"));
    setUrlState(sp.get("state"));
  }, []);

  // Add loading state to prevent multiple executions
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const processedCodeRef = useRef(null);
  const processedLoginErrorRef = useRef(null);
  const emitLoginEvent = useCallback((login_status, details = {}) => {
    emitGTMEvent({
      event: "login",
      method: details?.method || "tid",
      login_status,
      account_status: details?.account_status || null
    });

    // Legacy event so any GTM triggers still keyed on `login_success`
    // continue to fire alongside the unified `login` event.
    if (login_status === "success") {
      emitGTMEvent({
        event: "login_success",
        trimble_id: details?.trimble_id || null,
        logged_in: true,
        method: details?.method || null
      });
    }
  }, []);
  const getAccessToken = useCallback(async code_0 => {
    if (isLoadingToken) return; // Prevent multiple simultaneous requests

    setIsLoadingToken(true);
    emitLoginEvent("attempt");
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/x-www-form-urlencoded");
      const urlencoded = new URLSearchParams();
      urlencoded.append("grant_type", "authorization_code");
      urlencoded.append("redirect_uri", redirect_uri);
      urlencoded.append("code_verifier", code_verifier);
      urlencoded.append("client_id", client_id);
      urlencoded.append("code", code_0);
      urlencoded.append("state", path);

      // create our options
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: urlencoded,
        redirect: "follow"
      };
      const response = await fetch(tokenEndpoint, requestOptions);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json(); // Directly parse JSON

      if (data?.access_token) {
        markUserAsKnown();
        let userFromToken = undefined;
        let rawUserData = null;
        if (data?.id_token) {
          rawUserData = jwtDecode(data.id_token);
          userFromToken = {
            firstName: rawUserData?.given_name,
            lastName: rawUserData?.family_name,
            email: rawUserData?.email,
            img: rawUserData?.picture,
            id: rawUserData?.aud,
            tid: rawUserData?.sub
          };
        }
        const loginPayload = {
          token: data.access_token,
          authTime: String(Date.now()),
          idToken: data?.id_token ?? "",
          refreshToken: data?.refresh_token ?? "",
          ...(userFromToken !== undefined ? {
            user: userFromToken
          } : {})
        };
        loginDispatch?.({
          type: "SET_LOGIN",
          payload: loginPayload
        });
        if (rawUserData) {
          const authMethod = rawUserData?.identities?.[0]?.provider || rawUserData?.amr?.[0] || "tid";
          const accountStatus = rawUserData?.account_status || rawUserData?.accountStatus || null;
          syncStoredUserInfo(userFromToken);
          emitLoginEvent("success", {
            method: authMethod,
            account_status: accountStatus,
            trimble_id: rawUserData?.sub || null
          });
        }
      }
      dispatch?.({
        type: "SET_AUTH_CHECK_COMPLETE",
        payload: true
      });

      // Remove OAuth params from the URL. `state` must be stripped here
      // too (not just `code`/`identity_provider`) — leaving it behind made
      // every later refresh look like an orphaned post-OAuth return to
      // useAuthCheck's hasOrphanedState check, which then skipped
      // attempting a fresh silent-SSO login restore on every subsequent
      // refresh.
      const nextSearchParams = new URLSearchParams(window.location.search);
      nextSearchParams.delete("code");
      nextSearchParams.delete("state");
      nextSearchParams.delete("identity_provider");
      // Native replaceState (not router.replace) so Next can't fall back to a full reload that drops the in-memory login.
      const nextQuery = nextSearchParams.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`);
    } catch (error_0) {
      console.error("Error getting access token:", error_0);
      emitLoginEvent("fail");
      // Mark auth check complete even on failure to prevent useAuthCheck
      // from firing a silent SSO redirect loop when the code is still in the URL.
      dispatch?.({
        type: "SET_AUTH_CHECK_COMPLETE",
        payload: true
      });
      // Also strip the (now-consumed, invalid) OAuth params on failure —
      // otherwise a fresh mount (e.g. the user refreshing again) sees the
      // same stale `code` and retries the exact same failing exchange
      // forever, since processedCodeRef resets on every new mount.
      const failedSearchParams = new URLSearchParams(window.location.search);
      failedSearchParams.delete("code");
      failedSearchParams.delete("state");
      failedSearchParams.delete("identity_provider");
      const failedQuery = failedSearchParams.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${failedQuery ? `?${failedQuery}` : ""}${window.location.hash}`);
    } finally {
      setIsLoadingToken(false);
    }
  }, [client_id, redirect_uri, code_verifier, path, tokenEndpoint, dispatch, isLoadingToken, emitLoginEvent, loginDispatch]);
  useEffect(() => {
    // OAuth code: exchange for tokens if we haven't already processed this code and we're not authenticated
    if (!isAuthenticated && !isLoadingToken) {
      if (code && code !== "null" && processedCodeRef.current !== code) {
        processedCodeRef.current = code;
        getAccessToken(code);
      }
    }
  }, [code, isAuthenticated, isLoadingToken, getAccessToken, loginDispatch]);
  useEffect(() => {
    if (!error || processedLoginErrorRef.current === error) {
      return;
    }
    processedLoginErrorRef.current = error;
    emitLoginEvent("fail");
  }, [error, emitLoginEvent]);
};
export default useAuthTID;